/**
 * Cloudlift Auto-Upload Integration v2
 * Uses proper Cloudlift JavaScript API for linked designs from Design Library
 */
(function() {
  'use strict';
  
  // State management
  let cloudliftField = null;
  let hasUpload = false;
  let isProcessed = false;
  let linkedDesignUrl = null;
  
  /**
   * Get file URL from query parameters
   */
  function getValidFileUrl() {
    const params = new URLSearchParams(window.location.search);
    const fileParam = params.get('file');
    
    if (!fileParam) return null;
    
    try {
      const decodedUrl = decodeURIComponent(fileParam);
      if (decodedUrl.startsWith('http://') || decodedUrl.startsWith('https://')) {
        return decodedUrl;
      }
    } catch (e) {
      console.warn('Cloudlift Auto-Upload: Failed to decode file parameter:', e);
    }
    
    return null;
  }

  /**
   * Wait for Cloudlift field to be ready
   */
  function waitForCloudliftField() {
    return new Promise((resolve, reject) => {
      // Check if field is already available
      if (cloudliftField) {
        resolve(cloudliftField);
        return;
      }

      // Set up listener for upload:ready event
      const handleUploadReady = (e) => {
        cloudliftField = e.detail;
        console.log('Cloudlift Auto-Upload: Field ready');
        window.removeEventListener('upload:ready', handleUploadReady);
        resolve(cloudliftField);
      };

      window.addEventListener('upload:ready', handleUploadReady);

      // Timeout after 30 seconds
      setTimeout(() => {
        window.removeEventListener('upload:ready', handleUploadReady);
        reject(new Error('Cloudlift field not ready within timeout'));
      }, 30000);
    });
  }

  /**
   * Upload file using Cloudlift API
   */
  async function uploadFileToCloudlift(field, fileUrl) {
    try {
      console.log('Cloudlift Auto-Upload: Starting upload for:', fileUrl);
      
      // Use Cloudlift's uploadFile API with URL
      const file = await field.uploadFile(fileUrl);
      
      console.log('Cloudlift Auto-Upload: Upload successful:', file);
      return file;
    } catch (error) {
      console.error('Cloudlift Auto-Upload: Upload failed:', error);
      throw error;
    }
  }

  /**
   * Show linked design preview and hide Cloudlift widget
   */
  function showLinkedDesignPreview(fileUrl) {
    // Hide the Cloudlift widget since we have a linked design
    if (cloudliftField && cloudliftField.element) {
      cloudliftField.element.style.display = 'none';
    }

    // Create or show preview
    let preview = document.getElementById('cloudlift-linked-preview');
    if (!preview) {
      createLinkedPreview(fileUrl);
    } else {
      const thumbnail = preview.querySelector('img');
      if (thumbnail) {
        thumbnail.src = fileUrl;
      }
      preview.style.display = 'block';
    }

    // Set global variables for validation compatibility
    if (typeof window.linkedDesignUrl !== 'undefined') {
      window.linkedDesignUrl = fileUrl;
    }
    if (typeof window.currentFile !== 'undefined') {
      window.currentFile = { name: 'Linked Design', size: 0 };
    }

    // Trigger validation if available
    if (typeof window.validateFileUpload === 'function') {
      window.validateFileUpload();
    }
  }

  /**
   * Create linked design preview element
   */
  function createLinkedPreview(fileUrl) {
    // Find the Cloudlift widget container
    const cloudliftContainer = document.querySelector('[data-cloudlift-upload-field]') || 
                              document.querySelector('.cloudlift-upload-field') ||
                              cloudliftField?.element?.parentElement;

    if (!cloudliftContainer) {
      console.warn('Cloudlift Auto-Upload: Could not find container for preview');
      return;
    }

    const previewHtml = `
      <div id="cloudlift-linked-preview" class="cloudlift-linked-preview">
        <div class="cloudlift-linked-preview__content">
          <img src="${fileUrl}" alt="Design from library" class="cloudlift-linked-preview__thumbnail">
          <div class="cloudlift-linked-preview__info">
            <p class="cloudlift-linked-preview__text">🎨 Design from library</p>
            <button type="button" id="remove-cloudlift-linked" class="cloudlift-linked-preview__remove">
              ✕ Remove & Upload Different
            </button>
          </div>
        </div>
      </div>
    `;

    cloudliftContainer.insertAdjacentHTML('afterbegin', previewHtml);

    // Add remove button functionality
    const removeButton = document.getElementById('remove-cloudlift-linked');
    if (removeButton) {
      removeButton.addEventListener('click', removeLinkedDesign);
    }

    // Add styles
    addPreviewStyles();
  }

  /**
   * Remove linked design and show Cloudlift widget
   */
  function removeLinkedDesign() {
    // Show Cloudlift widget
    if (cloudliftField && cloudliftField.element) {
      cloudliftField.element.style.display = 'block';
    }

    // Hide preview
    const preview = document.getElementById('cloudlift-linked-preview');
    if (preview) {
      preview.remove();
    }

    // Clear global variables
    if (typeof window.linkedDesignUrl !== 'undefined') {
      window.linkedDesignUrl = null;
    }
    if (typeof window.currentFile !== 'undefined') {
      window.currentFile = null;
    }

    // Clear URL parameters
    const url = new URL(window.location);
    url.searchParams.delete('file');
    url.searchParams.delete('rtp');
    
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, document.title, url.toString());
    }

    // Reset state
    hasUpload = false;
    isProcessed = false;
    linkedDesignUrl = null;

    // Trigger validation
    if (typeof window.validateFileUpload === 'function') {
      window.validateFileUpload();
    }
  }

  /**
   * Add CSS styles for preview
   */
  function addPreviewStyles() {
    if (document.getElementById('cloudlift-linked-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'cloudlift-linked-styles';
    style.textContent = `
      .cloudlift-linked-preview {
        margin-bottom: 16px;
        padding: 12px;
        background-color: #e8f5e8;
        border: 2px solid #4CAF50;
        border-radius: 6px;
      }
      
      .cloudlift-linked-preview__content {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      
      .cloudlift-linked-preview__thumbnail {
        width: 60px;
        height: 60px;
        object-fit: cover;
        border-radius: 4px;
        border: 1px solid #4CAF50;
      }
      
      .cloudlift-linked-preview__info {
        flex: 1;
      }
      
      .cloudlift-linked-preview__text {
        margin: 0 0 8px 0;
        font-size: 14px;
        color: #2E7D32;
        font-weight: 500;
      }
      
      .cloudlift-linked-preview__remove {
        background: #ff9800;
        border: none;
        color: white;
        font-size: 12px;
        cursor: pointer;
        padding: 6px 10px;
        border-radius: 4px;
        font-weight: 500;
        transition: background-color 0.2s;
      }
      
      .cloudlift-linked-preview__remove:hover {
        background: #f57c00;
      }
      
      @media (max-width: 768px) {
        .cloudlift-linked-preview__content {
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
        }
        
        .cloudlift-linked-preview__remove {
          align-self: flex-end;
        }
      }
    `;
    
    document.head.appendChild(style);
  }

  /**
   * Setup form validation
   */
  function setupFormValidation() {
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    productForms.forEach(form => {
      form.addEventListener('submit', function(e) {
        if (!hasUpload && linkedDesignUrl) {
          e.preventDefault();
          e.stopPropagation();
          alert('Please wait for your design to finish uploading.');
          return false;
        }
      });
    });
  }

  /**
   * Main initialization function
   */
  async function initialize() {
    if (isProcessed) return;
    
    console.log('Cloudlift Auto-Upload: Initializing...');
    
    // Get file URL from query parameters
    linkedDesignUrl = getValidFileUrl();
    if (!linkedDesignUrl) {
      console.log('Cloudlift Auto-Upload: No file URL found');
      return;
    }
    
    console.log('Cloudlift Auto-Upload: Found file URL:', linkedDesignUrl);
    
    try {
      // Wait for Cloudlift field to be ready
      console.log('Cloudlift Auto-Upload: Waiting for Cloudlift field...');
      const field = await waitForCloudliftField();
      
      // Upload the file
      const uploadResult = await uploadFileToCloudlift(field, linkedDesignUrl);
      
      // Mark as successful
      hasUpload = true;
      isProcessed = true;
      
      console.log('Cloudlift Auto-Upload: Upload completed successfully');
      
      // Show preview
      showLinkedDesignPreview(linkedDesignUrl);
      
      // Setup form validation
      setupFormValidation();
      
    } catch (error) {
      console.error('Cloudlift Auto-Upload: Failed:', error);
      setupFormValidation();
    }
  }
  
  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
  
})();
